import React from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  PaginationState,
  VisibilityState,
  OnChangeFn,
} from "@tanstack/react-table";
import { ColumnVisibilityDropdown } from "./column-visibility-dropdown";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { Button } from "./button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { usePageSize } from "../../hooks/usePageSize";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pageCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  loading?: boolean;
  manualPagination?: boolean;
  manualSorting?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  onResetColumnVisibility?: () => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  pageCount = 0,
  pagination: controlledPagination,
  onPaginationChange,
  sorting: controlledSorting,
  onSortingChange,
  loading = false,
  manualPagination = false,
  manualSorting = false,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  onResetColumnVisibility,
}: DataTableProps<TData, TValue>) {
  const { setPageSize: savePageSize } = usePageSize();
  const [sorting, setSorting] = React.useState<SortingState>(controlledSorting || []);
  const [pagination, setPagination] = React.useState<PaginationState>(
    controlledPagination || {
      pageIndex: 0,
      pageSize: 25,
    }
  );

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      sorting: controlledSorting || sorting,
      pagination: controlledPagination || pagination,
      ...(controlledColumnVisibility !== undefined && { columnVisibility: controlledColumnVisibility }),
    },
    onColumnVisibilityChange: onColumnVisibilityChange,
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(controlledSorting || sorting) : updater;
      onSortingChange ? onSortingChange(newSorting) : setSorting(newSorting);
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(controlledPagination || pagination) : updater;
      onPaginationChange ? onPaginationChange(newPagination) : setPagination(newPagination);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    manualPagination,
    manualSorting,
  });

  return (
    <div className="space-y-4 min-w-0">
      {onColumnVisibilityChange && onResetColumnVisibility && (
        <div className="flex justify-end">
          <ColumnVisibilityDropdown table={table} onReset={onResetColumnVisibility} />
        </div>
      )}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup: any) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => {
                  const align = (header.column.columnDef.meta as any)?.align as
                    | 'left'
                    | 'center'
                    | 'right'
                    | undefined;
                  const headAlignClass =
                    align === 'center' ? 'text-center'
                      : align === 'right' ? 'text-right'
                      : '';
                  const justifyClass =
                    align === 'center' ? 'justify-center'
                      : align === 'right' ? 'justify-end'
                      : '';
                  const sortable = header.column.getCanSort();
                  return (
                    <TableHead key={header.id} className={headAlignClass}>
                      {header.isPlaceholder ? null : (
                        <div
                          className={[
                            sortable
                              ? 'flex items-center space-x-2 cursor-pointer select-none'
                              : align ? 'flex items-center' : '',
                            justifyClass,
                          ].filter(Boolean).join(' ')}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {sortable && (
                            <span className="ml-2">
                              {{
                                asc: <ArrowUp className="h-4 w-4" />,
                                desc: <ArrowDown className="h-4 w-4" />,
                              }[header.column.getIsSorted() as string] ?? (
                                <ArrowUpDown className="h-4 w-4 opacity-50" />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  className="h-24 text-center"
                >
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row: any) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="hover:bg-accent/50"
                >
                  {row.getVisibleCells().map((cell: any) => {
                    const align = (cell.column.columnDef.meta as any)?.align as
                      | 'left'
                      | 'center'
                      | 'right'
                      | undefined;
                    const cellAlignClass =
                      align === 'center' ? 'text-center'
                        : align === 'right' ? 'text-right'
                        : '';
                    return (
                      <TableCell key={cell.id} className={cellAlignClass}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  className="h-24 text-center text-muted-foreground"
                >
                  Brak danych do wyświetlenia
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center space-x-2">
          <p className="text-sm text-muted-foreground">
            Wierszy na stronę
          </p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              const newSize = Number(value);
              table.setPageSize(newSize);
              savePageSize(newSize);
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[25, 50, 100, 200].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">
              Strona {table.getState().pagination.pageIndex + 1} z{" "}
              {table.getPageCount()}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Pierwsza strona</span>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Poprzednia strona</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Następna strona</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Ostatnia strona</span>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
